// frontend/src/pages/Notifications.jsx
// Fixed 2026-10-29: the page read `n.read` / `res.pagination` and sent filters the server
// ignores, so read state and paging were wrong; mark-read / mark-all-read now use the
// server's PATCH routes (services/api.js). Delete is gone (the server has no delete route).
// Filters: All · Unread · Orders · Payments · Other — new orders are pinned first.
import { useState, useEffect, useCallback } from 'react';
import { notificationAPI } from '../services/api';
import { Card, Button, Spinner, Empty, SectionHeader } from '../components/ui';
import toast from 'react-hot-toast';
import { clsx } from 'clsx';
import { Bell, CheckCheck, RefreshCw, AlertCircle, Info, CheckCircle, TrendingUp, DollarSign, ShieldAlert, ShoppingBasket } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { categoryOf, asOrderAlert } from '../hooks/orderAlerts';

const TYPE_META = {
  transaction:   { icon: DollarSign,   color: '#00A8DB', label: 'Transaction' },
  kyc:           { icon: ShieldAlert,  color: '#F59E0B', label: 'KYC'         },
  investment:    { icon: TrendingUp,   color: '#8B5CF6', label: 'Investment'  },
  system:        { icon: Info,         color: '#3B82F6', label: 'System'      },
  alert:         { icon: AlertCircle,  color: '#EF4444', label: 'Alert'       },
  success:       { icon: CheckCircle,  color: '#10B981', label: 'Success'     },
};
const ORDER_META = { icon: ShoppingBasket, color: '#EF4444', label: 'New order' };

export const FILTERS = ['all', 'unread', 'orders', 'payments', 'other'];
const PAGE_SIZE = 50;
const WEEK_MS = 7 * 24 * 3600 * 1000;

function NotifCard({ n, onRead }) {
  const order = asOrderAlert(n);
  const meta = order ? ORDER_META : (TYPE_META[n.type] || TYPE_META.system);
  const Icon = meta.icon;
  const read = n.isRead === true;

  return (
    <div
      data-testid="notif"
      className={clsx(
        'flex gap-3 p-4 rounded-xl border transition-all group',
        read ? 'bg-bg-panel border-border opacity-70'
          : order ? 'bg-red/10 border-red shadow-card' : 'bg-bg-surface border-border-hi shadow-card'
      )}
    >
      <div
        className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5"
        style={{ background: `${meta.color}20`, border: `1px solid ${meta.color}40` }}
      >
        <Icon className="w-4 h-4" style={{ color: meta.color }} />
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2 flex-wrap">
            {order && !read && <span className="px-1.5 rounded bg-red text-white text-[10px] font-black">NEW ORDER</span>}
            <p className={clsx('text-sm font-semibold', read ? 'text-text-secondary' : 'text-text-primary')}>
              {n.title || 'Notification'}
            </p>
            {!read && <span className="w-2 h-2 rounded-full bg-cyan flex-shrink-0" />}
          </div>
          <span className="text-[10px] text-text-muted whitespace-nowrap flex-shrink-0">
            {n.createdAt ? formatDistanceToNow(new Date(n.createdAt), { addSuffix: true }) : '—'}
          </span>
        </div>
        <p className="text-text-muted text-xs mt-1 leading-relaxed">{n.body || n.message}</p>
      </div>

      {!read && (
        <div className="flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
          <button
            onClick={() => onRead(n._id)}
            className="p-1.5 rounded-lg text-text-muted hover:text-cyan hover:bg-cyan/10 transition-all"
            title="Mark as read"
          >
            <CheckCheck className="w-3.5 h-3.5" />
          </button>
        </div>
      )}
    </div>
  );
}

// What the list shows for a filter: category / unread, with unread new orders first.
export function visibleNotifications(list, filter) {
  const keep = list.filter(n => (['all', 'unread'].includes(filter) ? true : categoryOf(n) === filter));
  const pin = n => (asOrderAlert(n) && n.isRead !== true ? 0 : 1);
  return keep.map((n, i) => [n, i]).sort((a, b) => pin(a[0]) - pin(b[0]) || a[1] - b[1]).map(([n]) => n);
}

export default function Notifications() {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading]             = useState(true);
  const [filter, setFilter]               = useState('all');
  const [page, setPage]                   = useState(1);
  const [pagination, setPagination]       = useState({ total: 0, pages: 1 });
  const [unreadTotal, setUnreadTotal]     = useState(0);
  const [busy, setBusy]                   = useState('');

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: PAGE_SIZE };
      if (filter === 'unread') params.unread = 'true';
      const res = await notificationAPI.getAll(params);
      setNotifications(res.notifications || []);
      setPagination({ total: res.total || 0, pages: res.pages || 1 });
      setUnreadTotal(res.unreadCount || 0);
    } catch (err) {
      toast.error(err.message || 'Could not load notifications');
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  }, [page, filter]);

  useEffect(() => { fetchNotifications(); }, [fetchNotifications]);

  const handleRead = async (id) => {
    try {
      await notificationAPI.markRead(id);
      setNotifications(prev => prev.map(n => (n._id === id ? { ...n, isRead: true } : n)));
      setUnreadTotal(c => Math.max(0, c - 1));
    } catch (err) {
      toast.error(err.message || 'Failed to mark as read');
    }
  };

  const handleMarkAllRead = async () => {
    if (!window.confirm(`Mark all ${unreadTotal} unread notifications as read? Nothing is deleted.`)) return;
    setBusy('all');
    try {
      await notificationAPI.markAllRead();
      toast.success('All notifications marked as read');
      await fetchNotifications();
    } catch (err) {
      toast.error(err.message || 'Failed');
    } finally { setBusy(''); }
  };

  // Older than 7 days, among the ones on this page (marked one by one).
  const old = notifications.filter(n => n.isRead !== true && n.createdAt && Date.now() - new Date(n.createdAt).getTime() > WEEK_MS);
  const handleMarkOld = async () => {
    setBusy('old');
    try {
      for (const n of old) await notificationAPI.markRead(n._id);
      toast.success(`${old.length} older notification${old.length === 1 ? '' : 's'} marked as read`);
      await fetchNotifications();
    } catch (err) {
      toast.error(err.message || 'Failed');
    } finally { setBusy(''); }
  };

  const shown = visibleNotifications(notifications, filter);

  return (
    <div className="space-y-6 animate-slide-up max-w-3xl mx-auto">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-black text-text-primary tracking-tight">Notifications</h1>
            {unreadTotal > 0 && (
              <span className="bg-cyan text-bg text-xs font-black px-2.5 py-1 rounded-full">
                {unreadTotal} new
              </span>
            )}
          </div>
          <p className="text-text-secondary text-sm mt-1">Your alerts, orders, payment updates and system messages.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          {old.length > 0 && (
            <Button variant="ghost" size="sm" onClick={handleMarkOld} loading={busy === 'old'}>
              Mark read: older than 7 days ({old.length})
            </Button>
          )}
          {unreadTotal > 0 && (
            <Button variant="ghost" size="sm" onClick={handleMarkAllRead} loading={busy === 'all'}>
              <CheckCheck className="w-4 h-4" /> Mark all read
            </Button>
          )}
          <button
            onClick={fetchNotifications}
            className="text-text-muted hover:text-cyan p-2 rounded-xl hover:bg-cyan/10 transition-all"
            title="Refresh"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Filter pills */}
      <div className="flex gap-2 flex-wrap">
        {FILTERS.map(f => (
          <button
            key={f}
            onClick={() => { setFilter(f); setPage(1); }}
            className={clsx(
              'px-3 py-1.5 rounded-xl text-xs font-semibold capitalize transition-all',
              filter === f
                ? 'bg-cyan text-bg'
                : 'bg-bg-panel text-text-secondary border border-border hover:border-border-hi'
            )}
          >
            {f}
          </button>
        ))}
      </div>

      {/* Notifications list */}
      <Card>
        {loading ? (
          <div className="flex justify-center py-16"><Spinner size="lg" /></div>
        ) : shown.length === 0 ? (
          <Empty
            icon={<Bell className="w-12 h-12 text-text-muted" />}
            title="No notifications"
            description={filter === 'unread' ? "You're all caught up." : 'Notifications will appear here as activity happens.'}
          />
        ) : (
          <div className="space-y-2">
            {shown.map(n => <NotifCard key={n._id} n={n} onRead={handleRead} />)}
          </div>
        )}

        {pagination.pages > 1 && !loading && (
          <div className="flex items-center justify-between mt-5 pt-4 border-t border-border">
            <span className="text-text-muted text-xs">{pagination.total} total</span>
            <div className="flex gap-2">
              <Button variant="ghost" size="sm" disabled={page === 1} onClick={() => setPage(p => p - 1)}>
                Previous
              </Button>
              <span className="text-text-muted text-xs self-center">Page {page} of {pagination.pages}</span>
              <Button variant="ghost" size="sm" disabled={page >= pagination.pages} onClick={() => setPage(p => p + 1)}>
                Next
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Type legend */}
      <Card>
        <SectionHeader title="Notification Types" />
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {Object.entries({ order: ORDER_META, ...TYPE_META }).map(([key, { icon: Icon, color, label }]) => (
            <div key={key} className="flex items-center gap-2.5 p-2.5 rounded-xl bg-bg-panel border border-border">
              <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${color}20` }}>
                <Icon className="w-3.5 h-3.5" style={{ color }} />
              </div>
              <span className="text-text-secondary text-xs font-medium capitalize">{label}</span>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
